/**
 * Parameters for Cloudinary video transformations. Supports cropping mode and
 * aspect-ratio overrides applied when building video playback URLs.
 */
export interface VideoTransformations {
  /** The cropping method to use. */
  crop?: 'fill' | 'scale'
  /** The aspect ratio to use. */
  aspectRatio?: string
  /** The height of the video, in pixels. */
  height?: number
  /** The width of the video, in pixels. */
  width?: number
}

const transformationTokenMap: Record<keyof VideoTransformations, string> = {
  crop: 'c_',
  aspectRatio: 'ar_',
  height: 'h_',
  width: 'w_',
}

/**
 * A helper function for extracting the transformation values from the consumer-provided options.
 * @param t The transformations to apply to the video.
 * @returns {string[]} An array of transformation values (e.g. ['c_fill, ar_1:1]).
 * @category Utils
 */
const generateTransformationValues = (t: VideoTransformations): string[] => {
  const transformationValues = new Set(defaultTransformationTokens)

  Object.entries(transformationTokenMap).forEach(([key, token]) => {
    if (t[key]) {
      const value = `${token}${t[key]}`
      transformationValues.add(value)
    }
  })

  return [...transformationValues]
}

/**
 * Retrieves the transformed video URL.
 * @param url - The original video URL.
 * @param transformations - The transformations to apply to the video.
 * @returns The transformed video URL if successful, otherwise an empty string.
 * @category Utils
 */
export const getVideoTransformations = (url = '', transformations: VideoTransformations = {}): string => {
  const NAMED_TRANSFORM = 't_productvideo'
  const FORMAT_AUTO = 'f_auto:video'

  if (!url) {
    log.debug('[@domains/core/utils/getVideoTransformations] Please provide a url.')
    return ''
  }

  const { defaultTransformation = '' } = useAppConfig().assetTransformations ?? {}
  const enableApiAssetTransformations = useFeatureFlags().enableApiAssetTransformations

  const leadingTokens = enableApiAssetTransformations
    ? defaultTransformation.split(',').filter(Boolean)
    : []

  const extras = [...leadingTokens, ...generateTransformationValues(transformations)]
    .filter((t) => t !== FORMAT_AUTO)
    .join(',')

  if (url.includes(FORMAT_AUTO)) {
    if (!extras) {
      log.debug('[@domains/core/utils/getVideoTransformations] No op. The transformations have already been applied.')
      return url
    }
    if (leadingTokens.length > 0) {
      const [urlBeforeFormatAuto, pathAfterFormatAuto = ''] = url.split(`/${FORMAT_AUTO}/`)
      const restSegments = pathAfterFormatAuto.split('/').slice(1).join('/')
      return `${urlBeforeFormatAuto}/${FORMAT_AUTO}/${extras}/${restSegments}`
    }
    // Inject additional transformations after f_auto:video in an already-processed URL
    return url.replace(`/${FORMAT_AUTO}/`, `/${FORMAT_AUTO}/${extras}/`)
  }

  if (!url.includes('//assets.')) {
    log.debug('[@domains/core/utils/getVideoTransformations] Scene7 URL detected. Falling back to the legacy transformation logic.')
    return url
  }

  try {
    const urlObj = new URL(url)
    const parts = urlObj.pathname.split('/').filter(Boolean)

    // Find 'videos' segment
    const index = parts.indexOf('videos')
    if (index === -1) return url

    // Extract public_id: last 2 segments (folder/filename.mp4)
    const publicId = parts.slice(-2).join('/')
    if (!publicId.includes('.')) return url

    // Build additional transformations (c_fill, ar_1:1, etc.) — already computed above
    // Rebuild: videos/t_productvideo/f_auto:video[/extras]/public_id
    const transformChain = extras ? `${FORMAT_AUTO}/${extras}` : FORMAT_AUTO

    urlObj.pathname = `/videos/${NAMED_TRANSFORM}/${transformChain}/${publicId}`
    return urlObj.toString()
  }
  catch (err: any) {
    log.error(`[@domains/core/utils/getVideoTransformations] Unable to transform the provided URL: ${url}.`, err)
    return ''
  }
}
